/**
 * companiesHouseView — CH1 (slice plan v3): the WORDS for the "✦ Suggested from
 * Companies House" card. Every value is the server's — the search results, the
 * suggestion rows, why a row has no suggestion and when the profile was read;
 * nothing here decides a value.
 */
import type { CompaniesHouseField } from '../../../../api/types'
import { regionLabel, sectorLabel } from '../../../../lib/accountCodes'

/** The row's label (the PATCH field → the page's words). */
export const CH_FIELD_LABEL: Record<CompaniesHouseField['field'], string> = {
  legalEntity: 'Legal entity',
  companyNumber: 'Company number',
  companyStatus: 'Status',
  billingAddress: 'Billing address',
  sector: 'Industry',
  region: 'Region',
}

/** Companies House's status in words: "voluntary-arrangement" → "Voluntary arrangement". */
export function companyStatusWords(status: string | undefined): string {
  if (!status) return ''
  const s = status.replace(/-/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** A row's value in words: region and sector codes read as their labels. */
export function chValueWords(field: CompaniesHouseField['field'], value: string): string {
  if (!value) return ''
  if (field === 'region') return regionLabel(value) || value
  if (field === 'sector') return sectorLabel(value) || value
  if (field === 'companyStatus') return companyStatusWords(value)
  return value
}

/** Why a row has no suggestion (the server's note), in words. */
export function chNoteWords(note: CompaniesHouseField['note']): string | null {
  switch (note) {
    case 'sector_placeholder': return 'Filed as dormant or non-trading — no industry to suggest'
    case 'sector_none': return 'No industry codes filed'
    case 'region_straddling': return 'The postcode spans two regions — choose one'
    case 'region_unknown': return 'No region found for this postcode'
    default: return null
  }
}

/** "Active · read 1 Nov 2026" — a status is a fact at a time. */
export function statusWithReadTime(status: string | undefined, readAtDay: string | null): string {
  const s = companyStatusWords(status)
  if (!s) return ''
  return readAtDay ? `${s} · read ${readAtDay}` : s
}

/** The success toast after one Apply. */
export function chAppliedToast(field: CompaniesHouseField['field']): string {
  return `${CH_FIELD_LABEL[field]} applied from Companies House`
}
